import * as bundledChat from "./chat";
import type { WidgetConfig } from "./config";
import {
  mountWidget as mount,
  type MountWidgetDeps as MountDeps,
} from "./mount";

export type {
  MerchantFeedbackClient,
  ResolvedWidgetConfig,
  WidgetAppearance,
  WidgetConfig,
} from "./config";
export {
  MerchantFeedbackNetworkError,
  playgroundSessionSchema,
  widgetAppearanceSchema,
  widgetConfigSchema,
} from "./config";
export type {
  ResolvedMessage,
  WidgetChatMessage,
  WidgetMessageRole,
  WidgetProduct,
} from "./ui/message";
export { resolveMessage } from "./ui/message";
export type {
  MessageTheme,
  WidgetDirection,
  WidgetRadius,
  WidgetSurface,
  WidgetTheme,
  WidgetTokens,
} from "./ui/theme";
export {
  CHATBOX_THEME_PRESETS,
  deriveTheme,
  relativeLuminance,
  withAlpha,
} from "./ui/theme";
export type { WidgetPanelProps } from "./ui/widget-panel";
export { WidgetPanel } from "./ui/widget-panel";
export { DEFAULT_PANEL_SIZE } from "./ui/panel-resize";
export {
  mountWidgetPreview,
  type PromptTemplates,
  type WidgetPreviewOptions,
} from "./preview";
/** 预览里读到的挂件文案：后台测试与挂件读同一份 i18n 源，不抄字面量。 */
export {
  widget_preview_proactive as previewProactiveExampleText,
  widget_preview_product_title as previewProductTitleText,
  widget_proactive_dismiss as previewProactiveDismissLabel,
  widget_header_close as previewCloseLabel,
  widget_launcher_label as previewLauncherLabel,
} from "./paraglide/messages.js";
export type { ReplayOptions } from "./replay";
export { assistantHasFeedback, mountReplayMessage } from "./replay";
export type { AppearanceSource } from "./appearance";
export { readAppearance } from "./appearance";
export type {
  HandshakeFailed,
  HandshakeConfiguration,
  HandshakeOk,
  HandshakeResult,
} from "./protocol/handshake";
export { requestHandshake } from "./protocol/handshake";
export type {
  ConnectSocketInput,
  IoFactory,
  IoOptions,
  SendMessageAck,
  SendMessageFrame,
  SendMessageInput,
  SendMessageResult,
  SocketLike,
  WidgetSocket,
} from "./protocol/socket";
export { connectSocket } from "./protocol/socket";
export { createUid, getOrCreateUid, isPlaygroundUid } from "./protocol/uid";
export type { ChatModule } from "./chat";

export interface MountWidgetDeps {
  /** 拉取并执行完整聊天资源；同一 attempt 复用在途下载。缺省用打进同一包的模块——Playground 和现有调用方不用变。 */
  loadChat?: MountDeps["loadChat"];
  /** 低优先级预下载聊天资源（只下不执行）。缺省不预取。 */
  prefetchChat?: MountDeps["prefetchChat"];
  /** 页面预取中的公开握手响应；传输或读取失败时重新握手一次。 */
  handshake?: MountDeps["handshake"];
}

/**
 * 把 Widget 挂到宿主给的容器上，返回卸载函数。
 * 店面的 Theme App Extension 和后台的 Playground 都走这一个入口。
 * 样式隔在 Shadow DOM 里，主题的 CSS 进不来。
 * 挂载阶段只渲染角色入口、主动提示和等待卡片；完整聊天资源在角色画出后
 * 低优先级预下载（只下不执行），买家点入口、点提示或快照里有恢复价值的
 * 状态（开着、有未读、有在途发送）时复用同一次在途下载。
 * Playground 可通过容器 data-widget-surface="light|dark" 实时覆盖显示主题，
 * 不改已保存的配置、不重建会话；店面忽略此属性。
 */
export function mountWidget(
  container: HTMLElement,
  config: WidgetConfig,
  deps: MountWidgetDeps = {},
): () => void {
  return mount(container, config, {
    loadChat: deps.loadChat ?? (() => bundledChat),
    ...(deps.prefetchChat === undefined
      ? {}
      : { prefetchChat: deps.prefetchChat }),
    ...(deps.handshake === undefined ? {} : { handshake: deps.handshake }),
  });
}

export { clearPlaygroundState } from "./protocol/playground-state";
